import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  CalendarDays,
  FileText,
  LayoutDashboard,
  Send,
  Settings,
  TrendingUp,
  Users,
  Volume2,
} from 'lucide-react';

export type AdminNavItem = {
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  queueBadgeKey?: 'content' | 'audio' | 'publishing';
  requiredCapabilities?: string[];
};

export const adminNavItems: AdminNavItem[] = [
  {
    label: 'Mission Control',
    description: 'Queues, recent activity, and operational status.',
    href: '/admin',
    icon: LayoutDashboard,
  },
  {
    label: 'Content / Situations',
    description: 'Scenarios, dialog flows, readiness, and blockers.',
    href: '/admin/content',
    icon: BookOpen,
    queueBadgeKey: 'content',
    requiredCapabilities: ['content:write', 'content:review'],
  },
  {
    label: 'Event Catalog',
    description: 'Curated events, Event Pack templates, and publish gates.',
    href: '/admin/events',
    icon: CalendarDays,
    requiredCapabilities: ['content:write'],
  },
  {
    label: 'Audio',
    description: 'Missing clips, QA, pronunciation notes, and versions.',
    href: '/admin/audio',
    icon: Volume2,
    queueBadgeKey: 'audio',
    requiredCapabilities: ['audio:write'],
  },
  {
    label: 'Publishing',
    description: 'Preview, stage, publish, and rollback decisions.',
    href: '/admin/publishing',
    icon: Send,
    queueBadgeKey: 'publishing',
    requiredCapabilities: ['content:review', 'publish:write'],
  },
  {
    label: 'Users',
    description: 'User lookup, activity, and admin governance.',
    href: '/admin/users',
    icon: Users,
    requiredCapabilities: ['users:write'],
  },
  {
    label: 'Analytics',
    description: 'Usage, drop-off, retries, and quality signals.',
    href: '/admin/analytics',
    icon: TrendingUp,
    requiredCapabilities: ['analytics:read'],
  },
  {
    label: 'Audit Log',
    description: 'Immutable history of operational changes.',
    href: '/admin/audit',
    icon: FileText,
    requiredCapabilities: ['audit:read'],
  },
  {
    label: 'Settings / Taxonomy',
    description: 'Languages, categories, tags, and controlled vocabularies.',
    href: '/admin/settings',
    icon: Settings,
    requiredCapabilities: ['content:write'],
  },
];

export function getVisibleAdminNavItems(capabilities: string[]): AdminNavItem[] {
  const allowed = new Set(capabilities);

  return adminNavItems.filter((item) => {
    if (!item.requiredCapabilities || item.requiredCapabilities.length === 0) {
      return true;
    }

    return item.requiredCapabilities.some(
      (capability) => allowed.has('*') || allowed.has(capability)
    );
  });
}
